import { useEffect, useState } from "react";
import { Trophy, Check, Gift, Bell, Home, CalendarDays, BarChart3, Users, Crown, Flame, Swords, MessageCircle } from "lucide-react";
import { brandVars, backdropStyle, brandAsset } from "@/lib/brand";
import { LogoImg } from "@/components/LogoImg";
import { VerifiedBadge } from "@/components/VerifiedBadge";

export const SCREENS = ["Accueil", "Classement", "Pronos du soir", "Vie de la ligue"];
const RANK = [["Camille", 182, "+12"], ["Yanis", 176, "+8"], ["Toi", 171, "+15"], ["Inès", 160, "-2"], ["Hugo", 151, "+4"]];
const card = "rounded-2xl border border-white/10 bg-black/40 p-2.5 backdrop-blur";
const accent = { color: "var(--brand-accent)" };

function Brandmark({ m, size = "h-7 w-7" }) {
  const b = m.brand;
  if (b.logo_url) return <LogoImg src={brandAsset(b.logo_url)} fit={b.logo_fit} alt={`Logo ${m.name}`} className={`${size} shrink-0 rounded-lg`} />;
  return <span className={`${size} grid shrink-0 place-items-center rounded-lg text-[10px] font-extrabold`} style={{ background: "var(--brand-primary)", color: "var(--brand-on-primary)" }}>{m.name.slice(0, 2).toUpperCase()}</span>;
}

function Banner({ m, h = "h-20" }) {
  const b = m.brand;
  return b.banner_url
    ? <div className={`relative ${h} overflow-hidden rounded-2xl`}><LogoImg src={brandAsset(b.banner_url)} fit={b.banner_fit} alt="" className="!absolute inset-0 h-full w-full" /></div>
    : <div className={`${h} rounded-2xl p-3`} style={{ background: `linear-gradient(135deg, ${b.primary}, ${b.secondary})` }}><p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--brand-on-primary)" }}>Saison en cours</p></div>;
}

function Pick({ a, b, sel }) {
  return (
    <div className="grid grid-cols-2 gap-1.5">
      {[a, b].map((t) => (
        <span key={t} className="flex items-center justify-between rounded-lg border px-2 py-1.5 text-[10px] font-bold text-white" style={t === sel ? { borderColor: "var(--brand-accent)", background: "color-mix(in srgb, var(--brand-accent) 16%, transparent)" } : { borderColor: "rgba(255,255,255,.1)" }}>
          {t}{t === sel && <Check size={10} strokeWidth={3} style={accent} />}
        </span>
      ))}
    </div>
  );
}

function HomeScreen({ m }) {
  return (<>
    {m.brand.banner_url && <Banner m={m} h="h-14" />}
    <div>
      <p className="font-display text-xl font-extrabold uppercase leading-none text-white">Bonjour Camille</p>
      <p className="mt-1 truncate text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-300">{m.name} · 1er · 182 pts</p>
    </div>
    <div className={card}>
      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-300">Ma soirée</p>
      <div className="mt-1 grid grid-cols-3 text-center">
        {[["+24", "gagnés"], ["-6", "perdus"], ["+18", "bilan"]].map(([v, l], i) => <div key={l}><p className="font-mono-num text-sm font-bold" style={i === 2 ? accent : { color: i ? "#FCA5A5" : "#fff" }}>{v}</p><p className="text-[8px] text-slate-400">{l}</p></div>)}
      </div>
    </div>
    <div className={card}>
      <p className="mb-1.5 text-[10px] font-bold text-white">Ce soir 02:30 · Lakers vs Celtics</p>
      <Pick a="LAL" b="BOS" sel="LAL" />
      <p className="mt-1.5 text-[9px]" style={accent}>Prédiction enregistrée</p>
    </div>
    <div className={`${card} flex items-center gap-2`}><Crown size={14} style={accent} /><p className="text-[10px] text-white"><b>Yanis</b> est le roi de la semaine</p></div>
    {(m.brand.rewards || [])[0] && <div className="flex items-center gap-2 rounded-2xl p-2 text-[10px] font-bold" style={{ background: "var(--brand-button)", color: "var(--brand-on-button)" }}><Gift size={12} className="shrink-0" /><span className="truncate">À gagner : {m.brand.rewards[0].title}</span></div>}
  </>);
}

function StandingsScreen({ m }) {
  return (<>
    <Banner m={m} h="h-16" />
    <p className="font-display text-lg font-extrabold uppercase text-white">Classement</p>
    <div className="flex gap-1">{["Saison", "Mois", "Semaine"].map((t, i) => <span key={t} className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={i === 0 ? { background: "var(--brand-button)", color: "var(--brand-on-button)" } : { color: "#cbd5e1", border: "1px solid rgba(255,255,255,.12)" }}>{t}</span>)}</div>
    <div className={card}>
      {RANK.map(([n, p, d], i) => (
        <p key={n} className="flex items-center justify-between border-b border-white/5 py-1 text-[10px] text-white last:border-0" style={n === "Toi" ? { ...accent, fontWeight: 700 } : undefined}>
          <span className="flex items-center gap-1"><span className="w-3 text-slate-400">{i + 1}</span>{n}{i === 1 && <Crown size={9} style={accent} />}{i === 0 && <Trophy size={9} className="text-amber-300" />}</span>
          <span className="font-mono-num">{p} <span className="opacity-60">{d}</span></span>
        </p>
      ))}
    </div>
  </>);
}

function PicksScreen() {
  return (<>
    <p className="font-display text-lg font-extrabold uppercase text-white">Pronos du soir</p>
    {[["Lakers", "Celtics", "Lakers"], ["Knicks", "Heat", "Heat"], ["Nuggets", "Suns", null]].map(([a, b, s], i) => (
      <div key={a} className={card}>
        <p className="mb-1.5 flex justify-between text-[9px] text-slate-300"><span>{["01:00", "02:30", "04:00"][i]}</span>{i === 0 && <span className="font-bold" style={accent}>Match vedette ×2</span>}</p>
        <Pick a={a} b={b} sel={s} />
      </div>
    ))}
    <div className={card}>
      <p className="text-[9px] font-bold uppercase tracking-widest text-slate-300">Question bonus</p>
      <p className="mt-1 text-[10px] text-white">Meilleur marqueur du match ?</p>
      <p className="mt-1 rounded-lg px-2 py-1 text-[10px] font-bold" style={{ background: "var(--brand-button)", color: "var(--brand-on-button)" }}>LeBron James</p>
    </div>
  </>);
}

function LeagueScreen({ m }) {
  const rewards = (m.brand.rewards || []).slice(0, 2);
  return (<>
    <p className="font-display text-lg font-extrabold uppercase text-white">Vie de la ligue</p>
    <div className={card}>
      <p className="mb-1 flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-300"><Gift size={10} /> À gagner</p>
      {(rewards.length ? rewards : [{ title: "Maillot dédicacé" }, { title: "Places pour un match" }]).map((r, i) => <p key={i} className="truncate text-[10px] text-white"><span style={accent}>{r.place || (i === 0 ? "1er" : `${i + 1}e`)}</span> · {r.title || r}</p>)}
    </div>
    <div className={`${card} space-y-1`}>
      <p className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-300"><Swords size={10} /> Défi en cours</p>
      <p className="text-[10px] text-white">Camille vs Hugo · le perdant paie les cafés</p>
    </div>
    {[["Inès", "Qui croit encore aux Knicks ce soir ? 😅", MessageCircle], ["Yanis", "+31 pts hier soir, meilleur de la soirée", Flame]].map(([n, t, I]) => (
      <div key={n} className={`${card} flex gap-2`}><I size={12} className="mt-0.5 shrink-0" style={accent} /><p className="text-[10px] text-slate-200"><b className="text-white">{n}</b> {t}</p></div>
    ))}
  </>);
}

const VIEWS = [HomeScreen, StandingsScreen, PicksScreen, LeagueScreen];

function useTour(start) {
  const [i, setI] = useState(start % 4);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % 4), 3800);
    return () => clearInterval(t);
  }, [paused]);
  return { i, setI, setPaused };
}

export function BrandedPhone({ m, testId, screen = 0 }) {
  const b = m.brand;
  const { i: cur, setI, setPaused } = useTour(screen);
  const View = VIEWS[cur];
  const nav = [[Home, "Accueil"], [CalendarDays, "Matchs"], [BarChart3, "Classement"], [Users, "Ma ligue"]];
  const active = [0, 2, 1, 3][cur];
  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
    <div style={brandVars(b)} className="mpl-device mx-auto w-[260px] rounded-[40px] border border-white/15 bg-[#05070D] p-2.5 shadow-2xl shadow-black/60" data-testid={testId}>
      <div className="relative flex h-[500px] flex-col overflow-hidden rounded-[30px]" style={backdropStyle(b) || { backgroundColor: "#0B1020" }}>
        <div className="mx-auto mt-1.5 h-3.5 w-16 shrink-0 rounded-full bg-black" />
        <div className="flex shrink-0 items-center gap-2 px-3 py-2">
          <Brandmark m={m} />
          <p className="flex min-w-0 items-center gap-1 text-[11px] font-extrabold uppercase text-white"><span className="truncate">{m.name}</span><VerifiedBadge show={b.verified} size={12} testId={`${testId}-verified`} /></p>
          <Bell size={13} className="ml-auto shrink-0 text-slate-300" />
        </div>
        <div key={cur} className="mpl-rise min-h-0 flex-1 space-y-2.5 overflow-hidden px-3 pb-2" data-testid={`${testId}-screen-${cur}`}><View m={m} /></div>
        <div className="grid shrink-0 grid-cols-4 border-t border-white/10 bg-black/60 px-1 py-1.5 backdrop-blur">
          {nav.map(([I, l], i) => <span key={l} className="flex flex-col items-center gap-0.5 text-[8px]" style={i === active ? accent : { color: "#94a3b8" }}><I size={13} />{l}</span>)}
        </div>
      </div>
    </div>
    <div className="mt-4 flex items-center justify-center gap-1.5" data-testid={`${testId}-dots`}>
      {SCREENS.map((s, k) => (
        <button key={s} type="button" onClick={() => setI(k)} aria-label={`Voir l'écran ${s}`} aria-pressed={k === cur} data-testid={`${testId}-dot-${k}`}
          className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${k === cur ? "w-6 bg-white" : "w-1.5 bg-white/30 hover:bg-white/60"}`} />
      ))}
    </div>
    <p className="mt-1.5 text-center text-xs text-slate-400" data-testid={`${testId}-caption`}>Écran : {SCREENS[cur]}</p>
    </div>
  );
}
